import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * RestrictedAccess — inline wrapper for subscription-restricted content sections.
 * 3 states:
 * - Not authenticated → "Creeaza-ti un cont" message
 * - Authenticated but no subscription → "Activeaza un abonament" message
 * - Authenticated with subscription OR admin → shows children
 *
 * Props:
 * - compact: smaller styling for inline usage
 * - message: override default message (optional)
 */
const RestrictedAccess = ({
  children,
  compact = false,
  message,
}) => {
  const { isAuthenticated, hasActiveSubscription, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="h-7 max-w-72 rounded-md bg-gray-200 animate-pulse"></div>
    );
  }

  // Admin & paid subscribers see everything
  if (hasActiveSubscription) {
    return <>{children}</>;
  }

  // Authenticated but no subscription
  if (isAuthenticated) {
    return (
      <div
        className={`inline-flex items-center gap-2 border ${
          compact
            ? 'border-amber-300 bg-amber-50 py-1 px-2'
            : 'border-amber-300 bg-amber-50 py-2 px-3'
        } rounded-lg cursor-pointer hover:bg-amber-100 transition`}
        onClick={(e) => {
          e.stopPropagation();
          navigate('/abonamente');
        }}
        title="Activeaza un abonament pentru a vedea aceasta sectiune"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-600 shrink-0`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
        <span
          className={`text-amber-700 ${
            compact ? 'text-xs font-medium' : 'text-sm font-semibold'
          }`}
        >
          {message || 'Activeaza un abonament pentru a accesa aceasta sectiune'}
        </span>
      </div>
    );
  }

  // Not authenticated
  return (
    <div
      className={`inline-flex items-center gap-2 border ${
        compact
          ? 'border-gray-300 bg-gray-50 py-1 px-2'
          : 'border-gray-400 bg-gray-100 py-2 px-3'
      } rounded-lg cursor-pointer hover:bg-gray-200 transition`}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }}
      title="Autentifica-te pentru a vedea aceasta sectiune"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-500 shrink-0`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
      <span
        className={`text-gray-700 ${
          compact ? 'text-xs font-medium' : 'text-sm font-semibold'
        }`}
      >
        {message || 'Solicita o invitatie pentru a accesa aceasta sectiune'}
      </span>
    </div>
  );
};

export default RestrictedAccess;
